import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { AppStateFacade } from '@app-state';
import {
    ClientAuthErrorCodes,
    IAuthConfig,
} from '@models';
import {
    AuthService,
    ErrorMapperService,
    ServerConfiguration,
    StorageService,
} from '@services';
import { UiSnackBarService } from '@uipath/angular/components/ui-snackbar';
import { extractCookies } from '@uipath/angular/utilities';
import { filterNullOrUndefined } from '@utils/rxjs.utils';
import UiValidators, {
    UI_EMAIL_MAX_LENGTH,
    UI_NAME_LONG_MAX_LENGTH,
    UI_PASSWORD_MAX_LENGTH,
} from '@validators';

import {
    BehaviorSubject,
    Observable,
    Subject,
} from 'rxjs';
import {
    map,
    takeUntil,
} from 'rxjs/operators';

@Component({
    templateUrl: 'login.page.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginPageComponent implements OnDestroy {
    public loading$ = new BehaviorSubject(false);
    public form: FormGroup;
    public xsrfToken?: string;
    public provider?: string;
    public authConfig$: Observable<IAuthConfig>;
    public lastTenantName: string;
    public isTenantFieldVisible = false;
    public allowPasswordChange = true;
    public isRememberMeEnabled = false;

    public get externalUrl() {
        return `${this._serverConfig.baseHref}/Account/ExternalLogin`;
    }

    @ViewChild('externalLoginForm', { static: false })
    private _externalLoginForm?: ElementRef;

    private _destroyed$ = new Subject();

    constructor(
        errorMapperService: ErrorMapperService,
        fb: FormBuilder,
        public auth: AuthService,
        private _router: Router,
        private _cd: ChangeDetectorRef,
        private _snack: UiSnackBarService,
        private _serverConfig: ServerConfiguration,
        private _appState: AppStateFacade,
        private _storage: StorageService,
    ) {
        this.authConfig$ = this._appState.authConfig.value$;

        this.allowPasswordChange = !!this._appState.authConfig.value.cloud.allowPasswordChange;

        this.lastTenantName = auth.lastTenantUsed;

        this.form = fb.group({
            'tenancyName': [
                this.lastTenantName,
                Validators.maxLength(UI_NAME_LONG_MAX_LENGTH),
            ],
            'usernameOrEmailAddress': [
                '',
                Validators.compose([
                    Validators.required,
                    UiValidators.whitespace,
                    Validators.maxLength(UI_EMAIL_MAX_LENGTH),
                ]),
            ],
            'password': [
                '',
                Validators.compose([
                    Validators.required,
                    Validators.maxLength(UI_PASSWORD_MAX_LENGTH),
                ]),
            ],
        });

        if (this._appState.authConfig.value.isRememberMeEnabled) {
            this.isRememberMeEnabled = true;
            this.form.addControl('rememberMe', new FormControl(true));
        }

        const params = this._router.routerState.snapshot.root.queryParamMap;
        const ERROR_CODE_PARAM = 'errorCode';
        const paramErrorCode = Number(ClientAuthErrorCodes[params.get(ERROR_CODE_PARAM) as any]);

        if (!paramErrorCode) { return; }

        errorMapperService.auth
            .pipe(
                map((errorCodes) => errorCodes[params.get(ERROR_CODE_PARAM) as string]),
                filterNullOrUndefined(),
                takeUntil(this._destroyed$),
            )
            .subscribe(error => {
                this._snack.error(`${error} (#${paramErrorCode})`);
                this._router.navigate(['/account/login'], { queryParams: { returnUrl: params.get('returnUrl') } });
            });
    }

    ngOnDestroy() {
        this._destroyed$.next();
    }

    public switchTenant(event: Event) {
        event.preventDefault();
        this.isTenantFieldVisible = true;
        this.form.get('tenancyName')!.setValue('');
    }

    public externalLogin(provider: string) {
        this.provider = provider;
        this._appState.user
            .ensureCorrectIdentity()
            .subscribe(() => {
                this.xsrfToken = extractCookies()['XSRF-TOKEN'];
                this._cd.detectChanges();
                this._externalLoginForm!.nativeElement.submit();
            });
    }

    public onSubmit(event: Event) {
        event.preventDefault();
        if (!this.form.valid) {
            return;
        }
        this.loading$.next(true);

        this._appState.user.login(this.form.value)
            .subscribe((user) => {
                // store the tenant value only if the login is successful
                this.auth.lastTenantUsed = this.form.value.tenancyName;

                this._storage.set('loggedInUser', user.id.toString());

                const requestedRoute = this._router.routerState.snapshot.root.queryParams['returnUrl'];
                const { returnUrl, navigationParams } = this.auth.extractReturnUrl(requestedRoute);
                this._router.navigate([returnUrl], navigationParams);
            },
                async (httpErrorResponse) => {
                    this.loading$.next(false);
                    if (httpErrorResponse.status === 0) {
                        return;
                    }

                    // password expired so we need to redirect to user to change his
                    if (httpErrorResponse.status === 423) {
                        this.auth.lastTenantUsed = this.form.value.tenancyName;
                        await this._appState.user.ensureCorrectIdentity().toPromise();

                        const errorResponse = httpErrorResponse.error;

                        this._router.navigate(['/account/passwordexpired'], {
                            queryParams: {
                                userId: errorResponse.result.userId,
                            },
                        });
                    }
                });
    }
}
